import { useCallback, useEffect, useRef } from "react";

export const HERO_TRANSITION = {
  duration: 0.45,
  ease: [0.4, 0, 0.2, 1],
};

export const HERO_AUTOPLAY_MS = 5500;

/**
 * Reliable hero autoplay: chained timeouts (no interval drift), pauses on hover
 * and hidden tab, restarts cleanly after manual navigation.
 */
export function useHeroAutoplay({ count, paused, onAdvance }) {
  const timerRef = useRef(null);
  const countRef = useRef(count);
  const pausedRef = useRef(paused);
  const onAdvanceRef = useRef(onAdvance);
  const hiddenRef = useRef(
    typeof document !== "undefined" ? document.hidden : false
  );

  countRef.current = count;
  pausedRef.current = paused;
  onAdvanceRef.current = onAdvance;

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback(() => {
    clearTimer();
    if (pausedRef.current || hiddenRef.current || countRef.current <= 1) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onAdvanceRef.current();
      scheduleNext();
    }, HERO_AUTOPLAY_MS);
  }, [clearTimer]);

  const restart = useCallback(() => {
    scheduleNext();
  }, [scheduleNext]);

  useEffect(() => {
    scheduleNext();
    return clearTimer;
  }, [paused, count, scheduleNext, clearTimer]);

  useEffect(() => {
    const onVisibility = () => {
      hiddenRef.current = document.hidden;
      if (document.hidden) {
        clearTimer();
      } else {
        scheduleNext();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [clearTimer, scheduleNext]);

  return { restart };
}
